import hubspotIcon from "@/assets/integrations/hubspot.svg";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useHubSpotIntegration } from "@/contexts/hubspot-integration-provider";
import { useApi } from "@/hooks/useApi";
import { useTranslation } from "@/hooks/useTranslation";
import { EmployeeDTO, EmployeeExportFormat, HubSpotSaveToListSyncedRecordDTO } from "@shaarp/common";
import { ChevronsUpDownIcon, DownloadIcon, FileSpreadsheetIcon, Loader2Icon } from "lucide-react";
import { useState } from "react";
import { useToast, useToastError } from "../ui/use-toast";
import { EmployeesHubspotListExport } from "./employees-hubspot-list-export";

interface DataTableToolbarEmployeesExportProps {
  employees: EmployeeDTO[];
  companyId?: string | null;
  fetchDataLoading: boolean;
  disabled?: boolean;
  onEmployeesSynced?: (syncedRecords: HubSpotSaveToListSyncedRecordDTO[]) => void;
}

const CONTENT_TYPE: Record<EmployeeExportFormat, string> = {
  csv: "text/csv",
  xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
};

export function DataTableToolbarEmployeesExport({
  employees,
  companyId = null,
  fetchDataLoading,
  disabled = false,
  onEmployeesSynced,
}: DataTableToolbarEmployeesExportProps) {
  const toastError = useToastError();
  const api = useApi();
  const { toast } = useToast();
  const { t } = useTranslation();
  const { hubspotIntegration } = useHubSpotIntegration();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [hubspotDialogOpen, setHubspotDialogOpen] = useState(false);

  const exportEmployees = async (format: EmployeeExportFormat) => {
    const employeesIds = employees.map((e) => e.id);

    if (employeesIds.length === 0) {
      toast({
        variant: "default",
        title: t("components.dataTableEmployees.export.exportTitle"),
        description: t("components.dataTableEmployees.export.noEmployeeSelected"),
      });
      return;
    }

    setIsLoading(true);

    try {
      const response = await api.post<ArrayBuffer>(
        `/employee/export`,
        { employeesIds, format },
        { responseType: "arraybuffer" }
      );

      const blob = new Blob([response.data], { type: CONTENT_TYPE[format] });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;

      const fileName = `shaarp_export_${employeesIds.length}_contacts_${new Date().toISOString()}.${format}`;
      link.setAttribute("download", fileName);

      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast({
        variant: "default",
        title: t("components.dataTableEmployees.export.exportTitle"),
        description: t("components.dataTableEmployees.export.downloadStarting"),
      });
    } catch {
      toastError();
    } finally {
      setIsLoading(false);
    }
  };

  const showHubspot = hubspotIntegration?.isActive;
  const isDisabled = fetchDataLoading || isLoading || disabled || employees.length === 0;

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className="hidden h-8 lg:flex gap-2"
            disabled={isDisabled}
          >
            {isLoading ? (
              <Loader2Icon className="w-4 h-4 animate-spin" />
            ) : (
              <>
                <DownloadIcon className="w-4 h-4" />
                {showHubspot && (
                  <img src={hubspotIcon} alt="HubSpot" className="w-4 h-4" />
                )}
              </>
            )}
            <span>{t("components.dataTableEmployees.export.exportList")}</span>
            <ChevronsUpDownIcon className="w-3.5 h-3.5 ml-0.5 opacity-50" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent>
          {showHubspot && (
            <>
              <DropdownMenuGroup>
                <DropdownMenuItem onClick={() => setHubspotDialogOpen(true)}>
                  <img src={hubspotIcon} alt="HubSpot" className="w-4 h-4" />
                  <span>{t("components.dataTableEmployees.export.toHubspot")}</span>
                </DropdownMenuItem>
              </DropdownMenuGroup>
              <DropdownMenuSeparator />
            </>
          )}
          <DropdownMenuGroup>
            <DropdownMenuItem onClick={() => exportEmployees("csv")}>
              <FileSpreadsheetIcon className="w-4 h-4" />
              <span>{t("components.dataTableEmployees.export.formatCsv")}</span>
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => exportEmployees("xlsx")}>
              <FileSpreadsheetIcon className="w-4 h-4" />
              <span>{t("components.dataTableEmployees.export.formatExcel")}</span>
            </DropdownMenuItem>
          </DropdownMenuGroup>
        </DropdownMenuContent>
      </DropdownMenu>
      {showHubspot && (
        <EmployeesHubspotListExport
          employees={employees}
          companyId={companyId}
          open={hubspotDialogOpen}
          onOpenChange={setHubspotDialogOpen}
          onEmployeesSynced={onEmployeesSynced}
        />
      )}
    </>
  );
}
